import { useState } from "react";
import Reveal, { SectionLabel } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search } from "lucide-react";

const FAQS = [
  // Payments & Pricing
  {
    cat: "Payments & Pricing",
    q: "How much does Atlas cost?",
    a: "£450 — a one-time payment for lifetime access to the ATLAS Trading Indicator, the education vault and the community. The £450 is an introductory price and will increase after the next few sales; whenever you join, your price is locked in for life.",
  },
  {
    cat: "Payments & Pricing",
    q: "What payment methods do you accept?",
    a: "All major cards plus Apple Pay and Google Pay via Stripe's secure checkout. Prefer to split it? Clearpay offers 4 interest-free payments of £112.50, and Klarna offers instalment options — both subject to eligibility and the provider's own checks, shown to you at checkout. 18+ for instalment plans.",
  },
  {
    cat: "Payments & Pricing",
    q: "Is it really a one-time payment?",
    a: "Yes. One payment, lifetime access — the indicator, the community, the education vault and all future updates. No subscriptions, no upsells, no locked 'premium signal' tiers.",
  },
  {
    cat: "Payments & Pricing",
    q: "Can I get a refund?",
    a: "Because access is digital and instant, all sales are final. If something isn't working technically, the team will make it right — just message us on Telegram.",
  },
  // Access & Setup
  {
    cat: "Access & Setup",
    q: "How do I get the indicator after paying?",
    a: "Three steps, all shown on your confirmation page: check your email (your confirmation includes the exclusive Telegram invite link), submit your TradingView username on the thank-you page, and join the community. Access is then switched on for your username.",
  },
  {
    cat: "Access & Setup",
    q: "How long does indicator access take?",
    a: "Access is granted manually after you submit your TradingView username — usually within 24 hours, often much sooner. You'll be confirmed via email or Telegram once it's live on your account.",
  },
  {
    cat: "Access & Setup",
    q: "Which platforms does the indicator work on?",
    a: "The ATLAS Trading Indicator runs on TradingView — desktop, web and mobile — and works on TradingView's free plan, so there's nothing extra to buy. Setup instructions and personal onboarding are included.",
  },
  {
    cat: "Access & Setup",
    q: "I've paid but haven't received my email — what now?",
    a: "Check spam/junk first — first-time senders often land there. Still nothing after 10 minutes? Message us in the Telegram community with your order reference and the email you paid with, and we'll sort it immediately.",
  },
  // Markets & The Indicator
  {
    cat: "Markets & The Indicator",
    q: "Which markets does Atlas cover?",
    a: "Gold (XAUUSD) is the core focus, with coverage across NASDAQ, Silver, S&P 500, US30 and the major Forex pairs. Because the indicator runs on TradingView, you can apply it to any chart — the analysis and education centre on those markets.",
  },
  {
    cat: "Markets & The Indicator",
    q: "Is Atlas a signals service?",
    a: "No — and it never will be. Atlas gives you the indicator, the zones and the analysis framework, then teaches you to build your own bias and execute your own plan. The goal is that one day you don't need anyone's calls, including ours.",
  },
  {
    cat: "Markets & The Indicator",
    q: "What experience level do I need?",
    a: "Atlas is built for both ends: beginners get a structured curriculum that teaches analysis properly from day one, and experienced traders get sharper tools, deeper analysis and a serious community around them.",
  },
  // Community & Exclusive
  {
    cat: "Community & Exclusive",
    q: "Is there a free community I can try first?",
    a: "Yes — the free Atlas Telegram community is open to everyone: market discussion, weekly takeaways and a feel for how we work. Members who join Atlas unlock the exclusive rooms, with early analysis insights, deeper breakdowns and priority Q&A.",
  },
  {
    cat: "Community & Exclusive",
    q: "What exactly is Atlas Exclusive?",
    a: "The members-only side of Atlas: multi-market chart analysis across Gold, NASDAQ, Silver, S&P 500 and US30, early insights before moves develop, live chart demonstrations, session updates and structured educational support — everything inside the Exclusive Telegram rooms.",
  },
  {
    cat: "Broker",
    q: "Do I need to use a specific broker?",
    a: "No — Atlas works with any broker and we'll never ask you to switch. Separately, members have an optional partnered broker benefit (guided setup and any available account benefits), which you'll find in the Partnered Broker section above. Completely optional, and never part of the Atlas product itself.",
  },
  // Help & Support
  {
    cat: "Help & Support",
    q: "How do I get help after purchasing?",
    a: "Support lives in the community: message us in the exclusive Telegram room (or the free one) with your order reference. Access issues, setup help, platform questions — a human answers, usually fast. There's no ticket queue and no chatbot.",
  },
];

const Faq = () => {
  const [query, setQuery] = useState("");
  const filtered = FAQS.filter(
    (f) =>
      f.q.toLowerCase().includes(query.toLowerCase()) ||
      f.a.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <section id="faq" className="relative py-24 lg:py-36 bg-panel/30 border-y border-line/60" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel>Support & FAQ</SectionLabel>
          <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-50 leading-tight" data-testid="faq-title">
            Straight answers,
            <span className="text-gold-gradient"> no fine print.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative mt-9">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="faq-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a question…"
              className="w-full bg-ink/70 border border-line rounded-sm pl-11 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 transition-colors"
            />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
            {(query
              ? [{ cat: null, items: filtered }]
              : [...new Set(FAQS.map((f) => f.cat))].map((cat) => ({
                  cat,
                  items: FAQS.filter((f) => f.cat === cat),
                }))
            ).map((group) => (
              <div key={group.cat || "search"}>
                {group.cat && (
                  <p className="mt-8 mb-1 font-mono text-[10px] tracking-[0.3em] text-gold/70 uppercase" data-testid={`faq-cat-${group.cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {group.cat}
                  </p>
                )}
                {group.items.map((f) => {
                  const idx = FAQS.indexOf(f);
                  return (
                    <AccordionItem key={idx} value={`faq-${idx}`} className="border-line/70" data-testid={`faq-item-${idx + 1}`}>
                      <AccordionTrigger className="text-left font-display font-bold text-sm sm:text-base text-slate-100 hover:text-gold hover:no-underline py-5">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-6">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="py-8 text-sm text-slate-500" data-testid="faq-no-results">
                Nothing matches that — try another keyword, or ask us directly on Telegram.
              </p>
            )}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

export default Faq;
